import { Fade, Button, CircularProgress } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useInputMode } from "../../utils/hooks/useInputMode";

const FavButton = ({ children, onClick, actionsLoading, style = {}, onFocus }) => {
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress: onClick,
    onFocus,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <Button
      ref={ref}
      disabled={actionsLoading}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      className="cursor-pointer"
      sx={{
        height: "71px",
        background: focused ? "rgba(255,255,255,0.95)" : "rgba(50, 50, 50, 0.6)",
        fontSize: "32px",
        fontWeight: 600,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 3,
        py: 0,
        color: focused ? "#333" : "#F0F0F0",
        borderRadius: "10px",
        ml: 1.5,
        border: focused ? "2px solid #FFF" : "2px solid rgba(255,255,255,0.2)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        textShadow: focused ? "none" : "0 1px 2px rgba(0,0,0,0.8)",
        transition: "all 0.15s ease",
        // Apply filter to child images for icon color change
        '& img': {
          transition: 'filter 0.15s ease',
          ...(focused && {
            filter: 'brightness(0) saturate(100%) invert(20%) sepia(90%) saturate(2000%) hue-rotate(340deg)',
          }),
        },
        "&:hover": {
          background: "rgba(255,255,255,0.95)",
          color: "#333",
          border: "2px solid #FFF",
          textShadow: "none",
          '& img': {
            filter: 'brightness(0) saturate(100%) invert(20%) sepia(90%) saturate(2000%) hue-rotate(340deg)',
          },
        },
        ...style,
      }}
    >
      {actionsLoading ? <CircularProgress size={18} sx={{ color: focused ? "#333" : "#FFF" }} /> : children}
    </Button>
  );
};

/**
 * @param {string} contentKey - Content slug/key
 * @param {function} onFocus - Focus handler
 * @param {boolean} isMultiEpisode - If true, hide like/dislike (series/programs don't support parent-level likes)
 */
const ContentAction = ({ contentKey, onFocus, isMultiEpisode = false }) => {
  const { ref, focusKey } = useFocusable();
  const isLogin = localStorage.getItem("webTvUserToken");

  const [actionsLoading, setActionsLoading] = useState({
    bookmark: false,
    like: false,
    dislike: false,
    master: false,
  });

  const [actionData, setActionData] = useState({});

  const getContentActions = async () => {
    setActionsLoading(Object.assign({}, actionsLoading, { master: true }));

    try {
      const response = await httpService.get(getCMSApiRoute("action/content"), {
        params: {
          contentKey,
        },
      });

      if (response.status === 200) {
        const { data } = response.data;
        setActionData(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setActionsLoading(Object.assign({}, actionsLoading, { master: false }));
    }
  };

  const handleSaveBookmark = async () => {
    setActionsLoading(Object.assign({}, actionsLoading, { bookmark: true }));

    try {
      if (!actionData?.userActions?.bookmark) {
        await httpService.post(getCMSApiRoute("action/bookmark"), {
          contentKey,
        });

        setActionData({
          ...actionData,
          userActions: {
            ...actionData.userActions,
            bookmark: true,
          },
        });
      } else {
        await httpService.delete(getCMSApiRoute("action/bookmark"), {
          data: {
            contentKey,
          },
        });

        setActionData({
          ...actionData,
          userActions: {
            ...actionData.userActions,
            bookmark: false,
          },
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setActionsLoading(Object.assign({}, actionsLoading, { bookmark: false }));
    }
  };

  // Helper to get parent content's like/dislike status
  // For series/programs, find entry without season/episode (parent level)
  // For movies, use the first (and only) entry
  const getParentLikeStatus = () => {
    const likeOrDislikeArr = actionData?.userActions?.likeOrDislike || [];
    // Look for parent-level entry (no season/episode fields)
    const parentEntry = likeOrDislikeArr.find(
      (entry) => entry.season === undefined && entry.episode === undefined
    );
    // If found, use it; otherwise fall back to first entry (for movies)
    return parentEntry?.likeOrDislike ?? likeOrDislikeArr[0]?.likeOrDislike;
  };

  const parentLikeStatus = getParentLikeStatus();

  const handleLike = async ({ action }) => {
    if (actionsLoading.like || actionsLoading.dislike) return;
    if (action === "like" && parentLikeStatus === 1) return;
    if (action === "dislike" && parentLikeStatus === 2) return;

    setActionsLoading(
      Object.assign(
        {},
        actionsLoading,
        action === "like" ? { like: true } : { dislike: true }
      )
    );
    try {
      let response;
      if (action === "like") {
        response = await httpService.post(getCMSApiRoute("action/like"), {
          contentKey,
        });
        if (response.status === 201) {
          const { data } = response.data;

          if (data.actionType && data.actionType === 1) {
            setActionData({
              ...actionData,
              userActions: {
                ...actionData?.userActions,
                likeOrDislike: [
                  {
                    ...actionData?.userActions?.likeOrDislike?.[0],
                    likeOrDislike: 1,
                  },
                ],
              },
            });
          }
        }
      } else if (action === "dislike") {
        response = await httpService.post(getCMSApiRoute("action/dislike"), {
          contentKey,
        });

        if (response.status === 201) {
          const { data } = response.data;
          if (data.actionType && data.actionType === 2) {
            setActionData({
              ...actionData,
              userActions: {
                ...actionData?.userActions,
                likeOrDislike: [
                  {
                    ...actionData?.userActions?.likeOrDislike?.[0],
                    likeOrDislike: 2,
                  },
                ],
              },
            });
          }
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setActionsLoading(
        Object.assign(
          {},
          actionsLoading,
          action === "like" ? { like: false } : { dislike: false }
        )
      );
    }
  };

  useEffect(() => {
    getContentActions();
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <Fade in={isLogin && !actionsLoading.master} timeout={300}>
        <div className="relative h-100 flex" ref={ref}>
          {actionData?.userActions?.bookmark ? (
            <FavButton
              onClick={handleSaveBookmark}
              actionsLoading={actionsLoading.bookmark}
              onFocus={onFocus}
            >
              <div className="min-w-[32px]">
                <img src="/svg/bookmark-filled.svg" alt="Bookmarked" width={26} height={26} />
              </div>
            </FavButton>
          ) : (
            <FavButton
              onClick={handleSaveBookmark}
              actionsLoading={actionsLoading.bookmark}
              onFocus={onFocus}
            >
              <div className="min-w-[32px]">
                <img src="/svg/bookmark-empty.svg" alt="Bookmark" width={32} height={32} />
              </div>
            </FavButton>
          )}
          {/* Like/Dislike buttons - hidden for series/programs (multi-episode content) */}
          {!isMultiEpisode && (
            <>
              <FavButton
                style={{
                  padding: 1,
                  minWidth: "64px",
                }}
                onClick={() =>
                  handleLike({
                    action: "like",
                  })
                }
                actionsLoading={actionsLoading.like}
                onFocus={onFocus}
              >
                <div className="m-auto w-[32px] h-[32px]">
                  {parentLikeStatus === 1 ? (
                    <img src="/svg/thumbs-up-filled.svg" alt="Liked" width={32} height={32} />
                  ) : (
                    <img src="/svg/thumbs-up.svg" alt="Like" width={32} height={32} />
                  )}
                </div>
              </FavButton>
              <FavButton
                style={{
                  padding: 1,
                  minWidth: "64px",
                }}
                onClick={() =>
                  handleLike({
                    action: "dislike",
                  })
                }
                actionsLoading={actionsLoading.dislike}
                onFocus={onFocus}
              >
                <div className="m-auto w-[32px] h-[32px]">
                  {parentLikeStatus === 2 ? (
                    <img src="/svg/thumbs-down-filled.svg" alt="Disliked" width={32} height={32} />
                  ) : (
                    <img src="/svg/thumbs-down.svg" alt="Dislike" width={32} height={32} />
                  )}
                </div>
              </FavButton>
            </>
          )}
        </div>
      </Fade>
    </FocusContext.Provider>
  );
};

export default ContentAction;

